import React from 'react';

import { Add, Delete } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { StyledTreeItem } from 'src/components/TreeItem';

import { QuotaInputRow } from './QuotaInputRow';
import { EditableQuota } from '../../ficFundGroups';

type QuotaRecord = EditableQuota;

export type FundNodeData = {
  nodeId: string;
  name: string;
  cnpj?: string;
  walletFundId?: number;
  isFicParent: boolean;
  children: FundNodeData[];
  quotas: QuotaRecord[];
};

type FundNodeProps = {
  fund: FundNodeData;
  onUpdateQuota: (
    fundId: string,
    quotaId: string,
    field: keyof QuotaRecord,
    value: string | number,
  ) => void;
  onAddQuota: (fundId: string) => void;
  onRemoveQuota: (fundId: string, quotaId: string) => void;
  onRemoveFund: (fundId: string) => void;
  quotaErrors?: string[];
  onAddFicChild?: (parentWalletFundId: number) => void;
};

export function FundNode({
  fund,
  onUpdateQuota,
  onAddQuota,
  onRemoveQuota,
  onRemoveFund,
  quotaErrors = [],
  onAddFicChild,
}: FundNodeProps) {
  const walletFundId = fund.walletFundId;
  const hasQuotaError = (quotaId: string) => {
    return quotaErrors.includes(quotaId);
  };

  return (
    <StyledTreeItem
      key={fund.nodeId}
      nodeId={fund.nodeId}
      label={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
            pr: 2,
            pl: 1,
            borderRadius: 1,
            border: '1px solid #e0e0e0',
            my: 0.5,
          }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
            {fund.name}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              alignItems: 'center',
            }}>
            <Typography
              variant="caption"
              sx={{ textAlign: { xs: 'center', sm: 'left' } }}
              color="text.secondary">
              {fund.isFicParent
                ? `${fund.children.length} FIDC(s) monitorado(s)`
                : `${fund.quotas.length} cota(s)`}
            </Typography>
            {fund.isFicParent &&
              walletFundId !== undefined &&
              onAddFicChild && (
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={(event) => {
                    event.stopPropagation();
                    onAddFicChild(walletFundId);
                  }}
                  variant="outlined"
                  color="primary">
                  Adicionar FIDC
                </Button>
              )}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onRemoveFund(fund.nodeId);
              }}
              size="small"
              color="error"
              aria-label={
                fund.isFicParent ? 'Remover grupo FIC' : 'Remover fundo'
              }
              title={fund.isFicParent ? 'Remover grupo FIC' : 'Remover fundo'}
              sx={{
                '&:hover': {
                  backgroundColor: '#ffebee',
                },
              }}>
              <Delete />
            </IconButton>
          </Box>
        </Box>
      }>
      <Box sx={{ pl: 1, pb: 2 }}>
        {fund.isFicParent ? (
          <Box sx={{ pl: 1, pb: 1 }}>
            {fund.children.map((child) => (
              <FundNode
                key={child.nodeId}
                fund={child}
                onUpdateQuota={onUpdateQuota}
                onAddQuota={onAddQuota}
                onRemoveQuota={onRemoveQuota}
                onRemoveFund={onRemoveFund}
                quotaErrors={quotaErrors}
              />
            ))}
          </Box>
        ) : fund.quotas.length === 0 ? (
          <>
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                color: 'text.secondary',
                fontStyle: 'italic',
                backgroundColor: '#fafafa',
                border: '2px dashed #e0e0e0',
                borderRadius: 1,
                mx: 2,
              }}>
              Nenhuma cota adicionada. Clique em "Adicionar cota" para começar.
            </Box>

            <Button
              size="small"
              startIcon={<Add />}
              onClick={(e) => {
                e.stopPropagation();
                onAddQuota(fund.nodeId);
              }}
              variant="contained"
              color="primary"
              sx={{
                mt: 2,
                ml: 2,
                maxWidth: 140,
                minWidth: 'auto',
                px: 1,
                fontSize: '0.75rem',
              }}>
              Adicionar cota
            </Button>
          </>
        ) : (
          <Box display="flex" flexDirection="column" gap={1} my={2} px={2}>
            {fund.quotas.map((quota, index) => (
              <QuotaInputRow
                key={`${quota.id}-${index}`}
                fundId={fund.nodeId}
                quota={quota}
                index={index}
                fundCnpj={fund.cnpj}
                onUpdate={onUpdateQuota}
                onRemove={onRemoveQuota}
                hasError={hasQuotaError(quota.id)}
              />
            ))}
            <Divider />
            <Button
              size="small"
              startIcon={<Add />}
              onClick={(e) => {
                e.stopPropagation();
                onAddQuota(fund.nodeId);
              }}
              variant="contained"
              color="primary"
              sx={{
                maxWidth: 140,
                minWidth: 'auto',
                px: 1,
                fontSize: '0.75rem',
              }}>
              Adicionar cota
            </Button>
          </Box>
        )}
      </Box>
    </StyledTreeItem>
  );
}
